import React from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Tooltip from '@mui/material/Tooltip';
import { UICoverageByType, UIRepoWithTypedCoverage } from '../../../../../../api/teamMetricsApi';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  card: {
    padding: theme.spacing(3),
    border: '1px solid #E2E8F0',
    borderRadius: 4,
    backgroundColor: '#fff',
  },
  clickableCard: {
    cursor: 'pointer',
    transition: 'border-color 0.2s',
    '&:hover': {
      borderColor: '#0070F3',
    },
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  label: {
    fontSize: 10,
    fontWeight: 700,
    color: '#8E9299',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  infoIcon: {
    fontSize: 14,
    color: '#CBD5E0',
  },
  valueRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
  },
  mainValue: {
    fontSize: 36,
    fontWeight: 700,
    color: '#1A1A1A',
  },
  secondaryValue: {
    fontSize: 24,
    fontWeight: 600,
    color: '#4A5568',
  },
  trendPositive: {
    fontSize: 14,
    fontWeight: 700,
    color: '#38A169',
  },
  trendNegative: {
    fontSize: 14,
    fontWeight: 700,
    color: '#E53E3E',
  },
  chip: {
    fontSize: 10,
    fontWeight: 700,
    height: 20,
  },
  chipAbove: {
    backgroundColor: '#F0FFF4',
    color: '#22543D',
    border: '1px solid #C6F6D5',
  },
  chipBelow: {
    backgroundColor: '#FFFAF0',
    color: '#744210',
    border: '1px solid #FEEBC8',
  },
  subtitle: {
    fontSize: 11,
    color: '#8E9299',
    marginTop: theme.spacing(0.5),
  },
  chevron: {
    fontSize: 14,
    color: '#CBD5E0',
    marginLeft: 'auto',
  },
  viewPrsLink: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1.5),
    fontSize: 12,
    fontWeight: 600,
    color: '#0070F3',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  viewPrsIcon: {
    fontSize: 14,
  },
  statRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  statItem: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
  },
  statValue: {
    fontSize: 28,
    fontWeight: 700,
    color: '#1A1A1A',
  },
  statLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: '#718096',
  },
  statDivider: {
    width: 1,
    height: 32,
    backgroundColor: '#E2E8F0',
  },
  tooltipContent: {
    padding: theme.spacing(1),
  },
  tooltipRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  tooltipDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
  },
  tooltipLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: '#E2E8F0',
    minWidth: 70,
  },
  tooltipValue: {
    fontSize: 12,
    fontWeight: 600,
    color: '#fff',
    fontFamily: 'monospace',
  },
  needsAttentionList: {
    marginTop: theme.spacing(1),
  },
  needsAttentionItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 0),
    borderBottom: '1px solid #EDF2F7',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  repoName: {
    fontSize: 12,
    fontWeight: 500,
    color: '#2D3748',
    fontFamily: 'monospace',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 140,
  },
  lowCoverage: {
    fontSize: 12,
    fontWeight: 600,
    color: '#C53030',
    fontFamily: 'monospace',
  },
  warningIcon: {
    fontSize: 16,
    color: '#DD6B20',
    marginRight: theme.spacing(0.5),
  },
  allGoodText: {
    fontSize: 12,
    color: '#38A169',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
}));

const TEST_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  go_combined: { label: 'Combined', color: '#805AD5' },
  go_unit: { label: 'Unit', color: '#3182CE' },
  go_tas: { label: 'TAS', color: '#38A169' },
};

interface HealthSummaryProps {
  teamCoverageByType?: UICoverageByType[];
  orgCoverageByType?: UICoverageByType[];
  repoSummaries?: UIRepoWithTypedCoverage[];
  onPRImpactClick: () => void;
}

export const HealthSummary: React.FC<HealthSummaryProps> = ({
  teamCoverageByType,
  orgCoverageByType,
  repoSummaries,
  onPRImpactClick,
}) => {
  const classes = useStyles();

  const getCoverageByTestType = (coverages: UICoverageByType[] | undefined, testType: string) => {
    return coverages?.find((c) => c.testType === testType);
  };

  const formatDelta = (delta: number) => {
    const formatted = Math.abs(delta).toFixed(1);
    return delta >= 0 ? `+${formatted}%` : `-${formatted}%`;
  };

  const combinedTeam = getCoverageByTestType(teamCoverageByType, 'go_combined');
  const combinedOrg = getCoverageByTestType(orgCoverageByType, 'go_combined');
  const teamCombinedAvg = combinedTeam?.averageCoverage ?? 0;
  const teamCombinedDelta = combinedTeam?.averageDelta ?? 0;
  const orgCombinedAvg = combinedOrg?.averageCoverage ?? 0;
  const isAboveOrg = teamCombinedAvg >= orgCombinedAvg;
  const diff = Math.abs(teamCombinedAvg - orgCombinedAvg).toFixed(1);
  const isDeltaPositive = teamCombinedDelta >= 0;

  // Calculate totals from repo summaries
  const totalPRs = repoSummaries?.reduce((sum, repo) => sum + (repo.prCount ?? 0), 0) ?? 0;
  const totalDeploys = repoSummaries?.reduce((sum, repo) => sum + (repo.deploymentCount ?? 0), 0) ?? 0;

  // Find repos that need attention: top 5 active repos (with coverage data) below 50% combined coverage
  const activeReposWithCoverage = (repoSummaries ?? [])
    .filter((repo) => {
      if (repo.isExcluded) return false;
      if (repo.prCount === 0 && repo.deploymentCount === 0) return false;
      const combined = repo.coverage.find((c) => c.testType === 'go_combined');
      return combined && combined.coveragePercentage !== null;
    })
    .sort((a, b) => (b.prCount + b.deploymentCount) - (a.prCount + a.deploymentCount))
    .slice(0, 5);

  const needsAttention = activeReposWithCoverage.filter((repo) => {
    const combined = repo.coverage.find((c) => c.testType === 'go_combined');
    return combined!.coveragePercentage! < 50;
  });

  const renderCoverageTooltip = (coverages: UICoverageByType[] | undefined, showDelta: boolean = false) => (
    <Box className={classes.tooltipContent}>
      {['go_combined', 'go_unit', 'go_tas'].map((testType) => {
        const coverage = getCoverageByTestType(coverages, testType);
        const config = TEST_TYPE_CONFIG[testType];
        const value = coverage?.averageCoverage ?? 0;
        const delta = coverage?.averageDelta ?? 0;

        return (
          <Box key={testType} className={classes.tooltipRow}>
            <Box className={classes.tooltipDot} style={{ backgroundColor: config.color }} />
            <Typography className={classes.tooltipLabel}>{config.label}:</Typography>
            <Typography className={classes.tooltipValue}>
              {value.toFixed(1)}%
              {showDelta && (
                <span style={{ marginLeft: 8, color: delta >= 0 ? '#9AE6B4' : '#FEB2B2' }}>
                  {formatDelta(delta)}
                </span>
              )}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box className={classes.container}>
      {/* Card 1: Combined Coverage */}
      <Paper className={classes.card} elevation={0}>
        <Box className={classes.labelRow}>
          <Typography className={classes.label}>Combined Coverage</Typography>
          <Tooltip title="Combined code coverage across all selected team repos. The delta shows change since the start of the selected time period — positive means tests are covering more code." arrow placement="top">
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Tooltip>
        </Box>
        <Tooltip title={renderCoverageTooltip(teamCoverageByType, true)} arrow placement="top">
          <Box className={classes.valueRow} style={{ cursor: 'help' }}>
            <Typography className={classes.mainValue}>{teamCombinedAvg.toFixed(1)}%</Typography>
            <Typography className={isDeltaPositive ? classes.trendPositive : classes.trendNegative}>
              {formatDelta(teamCombinedDelta)}
            </Typography>
          </Box>
        </Tooltip>
        <Typography className={classes.subtitle}>
          {isDeltaPositive ? 'Increased' : 'Decreased'} since start of period
        </Typography>
      </Paper>

      {/* Card 2: vs Org Average */}
      <Paper className={classes.card} elevation={0}>
        <Box className={classes.labelRow} style={{ flexWrap: 'nowrap' }}>
          <Typography className={classes.label} style={{ whiteSpace: 'nowrap' }}>vs. Org Average</Typography>
          <Tooltip title="Average coverage across all non-excluded Weave repositories with coverage data. Compare your team's coverage to see how you stack up — being above average indicates strong test hygiene." arrow placement="top">
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Tooltip>
          <Chip
            label={`${diff}% ${isAboveOrg ? 'ABOVE' : 'BELOW'}`}
            size="small"
            className={`${classes.chip} ${isAboveOrg ? classes.chipAbove : classes.chipBelow}`}
            style={{ marginLeft: 'auto' }}
          />
        </Box>
        <Tooltip title={renderCoverageTooltip(orgCoverageByType)} arrow placement="top">
          <Box className={classes.valueRow} style={{ cursor: 'help' }}>
            <Typography className={classes.mainValue} style={{ color: '#4A5568' }}>
              {orgCombinedAvg.toFixed(1)}%
            </Typography>
          </Box>
        </Tooltip>
        <Typography className={classes.subtitle}>Organization-wide average</Typography>
      </Paper>

      {/* Card 3: PRs & Deploys */}
      <Paper
        className={`${classes.card} ${classes.clickableCard}`}
        elevation={0}
        onClick={onPRImpactClick}
      >
        <Box className={classes.labelRow}>
          <Typography className={classes.label}>Activity</Typography>
          <ChevronRightIcon className={classes.chevron} />
        </Box>
        <Box className={classes.statRow}>
          <Box className={classes.statItem}>
            <Typography className={classes.statValue}>{totalPRs}</Typography>
            <Typography className={classes.statLabel}>PRs</Typography>
          </Box>
          <Box className={classes.statDivider} />
          <Box className={classes.statItem}>
            <Typography className={classes.statValue}>{totalDeploys}</Typography>
            <Typography className={classes.statLabel}>Deploys</Typography>
          </Box>
        </Box>
        <Typography className={classes.subtitle}>During selected period</Typography>
        <Box className={classes.viewPrsLink}>
          <Typography component="span" style={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
            View all team PRs
          </Typography>
          <ArrowForwardIcon className={classes.viewPrsIcon} />
        </Box>
      </Paper>

      {/* Card 4: Needs Attention */}
      <Paper className={classes.card} elevation={0}>
        <Box className={classes.labelRow}>
          <Typography className={classes.label}>Needs Attention</Typography>
          <Tooltip title="Top 5 most active repos (by PR + deploy count) with coverage data that have less than 50% combined coverage." arrow placement="top">
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Tooltip>
        </Box>
        {needsAttention.length > 0 ? (
          <Box className={classes.needsAttentionList}>
            {needsAttention.slice(0, 3).map((repo) => {
              const combined = repo.coverage.find((c) => c.testType === 'go_combined');
              return (
                <Box key={repo.name} className={classes.needsAttentionItem}>
                  <Tooltip title={repo.name}>
                    <Typography className={classes.repoName}>{repo.name}</Typography>
                  </Tooltip>
                  <Typography className={classes.lowCoverage}>
                    {combined?.coveragePercentage?.toFixed(1) ?? 0}%
                  </Typography>
                </Box>
              );
            })}
            {needsAttention.length > 3 && (
              <Typography className={classes.subtitle} style={{ marginTop: 8 }}>
                +{needsAttention.length - 3} more
              </Typography>
            )}
          </Box>
        ) : (
          <Box mt={1}>
            <Typography className={classes.allGoodText}>
              Top 5 active repos above 50%
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default HealthSummary;
